import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  AppBar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  Icon,
  IconButton,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import clsx from "clsx";
import { makeStyles, styled } from "@mui/styles";
import Progress from "./Progress";
import AgGridTable from "../../utilities/AgGridTable";
import OAuthService from "src/app/auth/services/oAuthService";
import oAuthServiceConfig from "src/app/auth/services/oAuthService/oAuthServiceConfig";
import Select from "react-select";
import customStyles from "app/shared-components/SelectCustomStyle";
import ListPageHeader from "../../utilities/ListPageHeader";
import { confirmAlert } from "react-confirm-alert";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon/FuseSvgIcon";
import { toast } from "react-toastify";
import { grey } from "@mui/material/colors";
import AsyncSelect from "react-select/async";
import { LinearProgress } from "@material-ui/core";

const url = `${process.env.REACT_APP_DESIGN_STUDIO_API_KEY_URL}${oAuthServiceConfig.displayMasters}`;
const rangeUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.findRangeByVendor}`; // ranges/search/findRangeByVendor
const priceGroupUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.priceGroup}`;
const factoryVendorUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.factoryVendor}`;
const uploadFileForDisplayMaster = `${process.env.REACT_APP_DESIGN_STUDIO_API_KEY_URL}${oAuthServiceConfig.uploadFileForDisplayMaster}`;

const UploadButton = styled(Button)(({ theme }) => ({
  fontSize: 11,
  color: "#fff !important",
  background: `${theme.palette.primary.main}`,
  borderRadius: 20,
  padding: "5px 10px",
  cursor: "pointer",
}));

const Upload = (params) => {
  const downloadFile = `${process.env.REACT_APP_DESIGN_STUDIO_API_KEY_URL}${oAuthServiceConfig.downloadFile}`;
  const downloadPath = `${downloadFile}/${params.data?.uploadWinnerFile}?path=${params.data?.uploadWinnerPath}`;
  const hiddenFileInput = useRef();
  const [winner, setWinner] = useState({
    name: params.node.data.uploadWinnerFile,
  });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  async function remove() {
    // hiddenFileInput.current?.value = null;
    params.node.data.uploadWinnerFile = "";
    setWinner(null);
  }

  function uploadProgressChanged(params){
    const { progress } = params;
    setUploadProgress(progress * 100);
  }
  const onChange = async (e) => {
    const fileUploaded = e.target.files[0];
    setUploading(true);
    if (!fileUploaded) return;
    const payload = {
      uploadWinnerFile: fileUploaded,
    };
    const response = await OAuthService.addFiles(
      `${uploadFileForDisplayMaster}?srNo=${params.data.srNo}`,
      payload,
      uploadProgressChanged
    );
    toast.success("Upload Successfully");
    params.node.updateData(response.data);
    setWinner(fileUploaded);
    setUploading(false);
    setUploadProgress(0);
  };

  const downloadFiles = async () => {
    const blob = await fetch(downloadPath, OAuthService.config()).then((resp) =>
      resp.blob()
    );
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = params.node?.data?.uploadWinnerFile;
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(objUrl);
    document.body.removeChild(a);
  };

  return uploading ? <Box sx={{ display: 'flex', alignItems: 'center' }}>
     <Box sx={{ width: '100%', mr: 1 }}>
        <LinearProgress variant="determinate" value={uploadProgress} /> 
     </Box>
     <Box sx={{ minWidth: 35 }}>
     <Typography variant="body2" color="text.secondary">
      {Math.round(uploadProgress)}%
     </Typography>
     </Box>
    </Box> :
  <span>
      {!params.node.data.uploadWinnerFile && (
        <UploadButton
          variant="contained"
          component="label"
          fullWidth
          color="primary"
          ref={hiddenFileInput}
          onChange={onChange}
        >
          UPLOAD WINNER
          <input type="file" hidden />
        </UploadButton>
      )}
      {winner && (
        <>
          <Button
            variant="outlined"
            size="small"
            onClick={downloadFiles}
            startIcon={<FuseSvgIcon>heroicons-outline:download</FuseSvgIcon>}
          >
            Download
          </Button>
          <IconButton onClick={remove}>
            <FuseSvgIcon>heroicons-outline:x</FuseSvgIcon>
          </IconButton>
        </>
      )}
    </span>
};

export default function DisplayMasters(props) {
  const { open, handleClose } = props;
  const [rowData, setRowData] = useState();
  const [error, setError] = useState();
  const [snackState, setSnackState] = useState(false);
  const [successMsg, setSuccessMsg] = useState();
  const gridRef = useRef();
  const [addedRows, setAddedRows] = useState([]);
  const [productId, setProductId] = useState([]);
  const [range1Id, setRange1Id] = useState([]);
  const [range2Id, setRange2Id] = useState([]);
  const [showPaginationDropdown, setShowPaginationDropdown] = useState(false);

  const useStyles = makeStyles((theme) => ({
    root: {
      "& li": {
        padding: "5px 10px",
        cursor: "pointer",

        "&:hover": {
          backgroundColor: grey[800],
        },
      },
    },
  }));

  const CellSuggestionsEditor = forwardRef((props, ref) => {
    const classes = useStyles(props);
    const { colDef, value } = props;
    const [values, setValues] = useState(
      props.api
        .getRenderedNodes()
        .map((node) => node.data[colDef.field])
        .filter((v) => v.replaceAll(" ", "").length > 0)
    );
    const [filteredValues, setFilteredValues] = useState([]);
    const inputRef = useRef(null);
    const [currentValue, setCurrentValue] = useState(value);
    useEffect(() => {
      inputRef.current.focus();
      setFilteredValues(values);
    }, []);
    const _handleChange = (e) => {
      const { value } = e.target;
      setCurrentValue(value);
      setFilteredValues(values.filter((val) => val.includes(value)));
    };
    useImperativeHandle(ref, () => {
      return {
        getValue: () => currentValue,
      };
    });
    return (
      <div ref={ref} className={clsx(classes.root)}>
        <TextField
          ref={inputRef}
          value={currentValue}
          onChange={_handleChange}
          fullWidth
        />
        {currentValue !== "" && currentValue !== null && (
          <Box component={"ul"} className="suggestions">
            {filteredValues.map((fv) => (
              <li
                className="suggestion-value"
                onClick={() => setCurrentValue(fv)}
              >
                {fv}
              </li>
            ))}
          </Box>
        )}
      </div>
    );
  });

  const SelectProduct = forwardRef((props, ref) => {
    const { data, colDef } = props;
    const refVendor = useRef();
    const [error, setError] = useState("");
    const [vendors, setVendors] = useState([]);
    const [selectedVendor, setSelectedVendor] = useState(null);

    const getVendorsList = async () => {
      OAuthService.getList(factoryVendorUrl + `?size=1000`)
        .then((list) => {
          const { factoryVendors } = list.data?._embedded;
          const activeVender = factoryVendors.filter((item) => item.active);
          setVendors(
            activeVender?.map((vendor) => ({
              label: vendor.name,
              value: vendor.id,
            }))
          );
          const vendor = factoryVendors.filter(
            (vendor) => vendor.name === data[colDef.field]
          );
          if (vendor.length > 0)
            setSelectedVendor({ value: vendor[0].id, label: vendor[0].name });
        })
        .catch((err) => setError(err.message));
    };

    const handleVendorChange = (value) => {
      if (!value) return;
      data.productId = value.value;
      props.node.setData(data);
      setSelectedVendor(value);
      // setOpenCom(false);
    };

    useEffect(() => {
      getVendorsList();
    }, []);

    useImperativeHandle(ref, () => {
      return {
        getValue() {
          return selectedVendor?.label;
        },
      };
    });

    return (
      <div className="w-full" style={{ width: 200 }}>
        <Select
          className="basic-multi-select mb-4"
          name="name"
          required
          ref={refVendor}
          value={selectedVendor}
          onChange={handleVendorChange}
          options={vendors}
          styles={customStyles()}
        />
      </div>
    );
  });

  const SelectRange = forwardRef((props, ref) => {
    const { data, colDef, node } = props;
    const refRange = useRef();
    const [range, setRange] = useState([]);
    const [selectedRange, setSelectedRange] = useState(null);
    const [allItems, setAllItems] = useState([]);

    if (!productId) return;
    const getRangeList = async () => {
      if (!data.product) {
        alert("Please select a product first");
        return;
      }

      const rangeUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.findRangeByVendor}`;
      const list = await OAuthService.getList(`${rangeUrl}?vendorId=${data.productId}`);
      const ranges = list.data?._embedded?.ranges;

      setAllItems(ranges);
      setRange(
        ranges.map((range) => ({
          value: range.id,
          label: range.productName,
        }))
      );
      
      return ranges.map((range) => ({
        value: range.id,
        label: range.productName,
      }));
    };

    const handleRangeChange = (value) => {
      if (!value) return;

      const priceUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.ranges}`;

      const selectedRange = allItems.filter(
        (item) => item.id === value.value
      )[0];

      const priceGroupId = selectedRange.id
      OAuthService.getList(`${priceUrl}/${priceGroupId}/priceGroup`)
      .then((list) => {
        if (colDef.field === "range1"){
          data.priceGroup = list.data.pgId;
        } else if (colDef.field === "range2") {
          data.priceGroup2 = list.data.pgId;
        }
      })
      .catch((err) => setError(err.message));  
      data.range1Id = value.value;
      props.node.setData(data);
      setSelectedRange(value);
    };

    useEffect(() => {
      getRangeList();
    }, []);

    useImperativeHandle(ref, () => {
      return {
        getValue() {
          return selectedRange?.label;
        },
      };
    });

    return (
      <div className="w-full" style={{ width: 200 }}>
        <Select
          className="basic-multi-select mb-4"
          name="name"
          required
          ref={refRange}
          value={selectedRange}
          onChange={handleRangeChange}
          options={range}
          styles={customStyles()}
        />
      </div>
    );
  });

  const SelectCarCass = forwardRef((props, ref) => {
    const { data, colDef } = props;
    const refCarCass = useRef();
    const [carCass, setCarCass] = useState([]);
    const [selectedCarCass, setSelectedCarCass] = useState(null);
    const [allItems, setAllItems] = useState([]); 

    if (!productId) return;
    const getCarCassList = async () => {
      if (!data.product) {
        alert("Please select a product first");
        return;
      }
      const carcassUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.findAllCarcassByVendor}`;
      const list = await OAuthService.getList(`${carcassUrl}?vendorId=${data.productId}`);
      console.log('carcasses', list, data.productId)
      const carcasses = list.data?._embedded?.carcasses;

      setCarCass(
        carcasses.map((product) => ({
          value: `${product.productId} ${product.productName}`,
          label: `${product.productId} ${product.productName}`,
        }))
      );

      return carcasses.map((product) => ({
        value: `${product.productId} ${product.productName}`,
        label: `${product.productId} ${product.productName}`,
      }))
    };

    const handleCarCassChange = (value) => {
      if (!value) return;
      setSelectedCarCass(value);
    };

    useEffect(() => {
      getCarCassList();
    }, []);

    useImperativeHandle(ref, () => {
      return {
        getValue() {
          return selectedCarCass?.label;
        },
      };
    });

    return (
      <div className="w-full" style={{ width: 200 }}>
        <Select
          className="basic-multi-select mb-4"
          name="name"
          required
          ref={refCarCass}
          value={selectedCarCass}
          onChange={handleCarCassChange}
          options={carCass}
          styles={customStyles()}
        />
      </div>
    );
  });

  const SelectFront = forwardRef((props, ref) => {
    console.log('Front Color', props)
    const { data, colDef } = props;
    const refFront = useRef();
    const [front, setFront] = useState([]);
    const [selectedFront, setSelectedFront] = useState(null);
    
    if (!range1Id) return;
    const getFrontList = async () => {
      if (!data.range1) {
        alert("Please select a range first");
        return;
      }
      const colorUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.ranges}`;
      const data1 = { materialStatus: "true" };
      const list = await OAuthService.getList(`${colorUrl}/${data.range1Id}/variations`);
      const rangeVariations = list.data?._embedded?.rangeVariations;
      
      setFront(
        rangeVariations.map((product) => ({
          value: `${product.productId} ${product.productName}`,
          label: `${product.productId} ${product.productName}`,
        }))
      );
      
      return rangeVariations.map((product) => ({
        value: `${product.productId} ${product.productName}`,
        label: `${product.productId} ${product.productName}`,
      }))
    };

    const handleFrontChange = (value) => {
      if (!value) return;
      setSelectedFront(value);
    };

    useEffect(() => {
      getFrontList();
    }, []);

    useImperativeHandle(ref, () => {
      return {
        getValue() {
          return selectedFront?.label;
        },
      };
    });

    return (
      <div className="w-full" style={{ width: 200 }}>
        <Select
          className="basic-multi-select mb-4"
          name="name"
          required
          ref={refFront}
          value={selectedFront}
          onChange={handleFrontChange}
          options={front}
          styles={customStyles()}
        />
      </div>
    );
  });

  const SelectHandle = forwardRef((props, ref) => {
    const { data, colDef } = props;
    const refHandle = useRef();
    const [handle, setHandle] = useState([]);
    const [selectedHandle, setSelectedHandle] = useState(null);

    const getHandleList = async () => {
      const handleUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.findAllHandlesByVendor}`;
      const list = await OAuthService.getList(`${handleUrl}?vendorId=${data.productId}`);
      const handles = list.data?._embedded?.handles;

      setHandle(
        handles.map((product) => ({
          value: `${product.productId} ${product.productName}`,
          label: `${product.productId} ${product.productName}`,
        }))
      );
      
      return handles.map((product) => ({
        value: `${product.productId} ${product.productName}`,
        label: `${product.productId} ${product.productName}`,
      }))
    };

    const handleHandleChange = (value) => {
      if (!value) return;
      setSelectedHandle(value);
    };

    useEffect(() => {
      getHandleList();
    }, []);

    useImperativeHandle(ref, () => {
      return {
        getValue() {
          return selectedHandle?.label;
        },
      };
    });

    return (
      <div className="w-full" style={{ width: 200 }}>
        <Select
          className="basic-multi-select mb-4"
          name="name"
          required
          ref={refHandle}
          value={selectedHandle}
          onChange={handleHandleChange}
          options={handle}
          styles={customStyles()}
        />
      </div>
    );
  });

  const [columnDefs, setColumnDefs] = useState([
    {
      headerName: "Sno.",
      valueGetter: "node.rowIndex + 1",
      maxWidth: 100,
      editable: false,
    },
    { field: "reference", minWidth: 200 },
    { field: "layoutName", minWidth: 200 },
    {
      field: "product",
      cellEditorPopup: true,
      cellEditor: "agRichSelectCellEditor",
      cellEditor: SelectProduct,
      minWidth: 202,
    },
    {
      field: "range1",
      cellEditorPopup: true,
      cellEditor: "agRichSelectCellEditor",
      cellEditor: SelectRange,
      minWidth: 200,
    },
    {
      field: "priceGroup",
      minWidth: 200,
      editable: false,
    },
    {
      field: "front1",
      cellEditorPopup: true,
      cellEditor: "agRichSelectCellEditor",
      cellEditor: SelectFront,
      minWidth: 200,
    },
    {
      field: "carcass",
      cellEditorPopup: true,
      cellEditor: "agRichSelectCellEditor",
      cellEditor: SelectCarCass,
      minWidth: 250,
    },
    {
      field: "range2",
      cellEditorPopup: true,
      cellEditor: "agRichSelectCellEditor",
      cellEditor: SelectRange,
      minWidth: 200,
    },
    {
      field: "priceGroup2",
      editable: false,
      minWidth: 200,
    },
    {
      field: "front2",
      cellEditorPopup: true,
      cellEditor: "agRichSelectCellEditor",
      cellEditor: SelectFront,
      minWidth: 200,
    },
    {
      field: "carcass2",
      cellEditorPopup: true,
      cellEditor: "agRichSelectCellEditor",
      cellEditor: SelectCarCass,
      minWidth: 200,
    },
    {
      field: "scheme",
      minWidth: 200,
      cellEditorPopup: true,
      cellEditor: CellSuggestionsEditor,
    },
    {
      field: "handleConcept",
      minWidth: 200,
      cellEditorPopup: true,
      cellEditor: CellSuggestionsEditor,
    },
    {
      field: "handle1",
      cellEditorPopup: true,
      cellEditor: "agLargeTextCellEditor",
      cellEditor: SelectHandle,
      minWidth: 200,
    },
    {
      field: "handle2",
      cellEditorPopup: true,
      cellEditor: "agLargeTextCellEditor",
      cellEditor: SelectHandle,
      minWidth: 200,
    },
    {
      field: "drawerAccFinish",
      minWidth: 200,
      cellEditorPopup: true,
      cellEditor: CellSuggestionsEditor,
    },
    {
      field: "drawerType",
      minWidth: 200,
      cellEditorPopup: true,
      cellEditor: CellSuggestionsEditor,
    },
    {
      field: "antiStpMat",
      minWidth: 200,
      cellEditorPopup: true,
      cellEditor: CellSuggestionsEditor,
    },
    {
      field: "lightings",
      minWidth: 200,
      cellEditorPopup: true,
      cellEditor: CellSuggestionsEditor,
    },
    {
      field: "sinkAccsseories",
      minWidth: 200,
      cellEditorPopup: true,
      cellEditor: CellSuggestionsEditor,
    },
    {
      field: "workTops",
      minWidth: 200,
      cellEditorPopup: true,
      cellEditor: CellSuggestionsEditor,
    },
    {
      field: "plinths",
      minWidth: 200,
      cellEditorPopup: true,
      cellEditor: CellSuggestionsEditor,
    },
    {
      field: "uploadWinnerFile",
      minWidth: 200,
      editable: false,
      cellRenderer: Upload,
    },
    { field: "remarks", minWidth: 200 },
  ]);

  const loadDate = async () => {
    try {
      const list = await OAuthService.getList(url + "?size=1000");
      let displayMasters = list.data?._embedded?.displayMasters;
      setRowData(displayMasters);
    } catch (error) {
      setError(error.message);
      console.log(error);
    }
  };

  const onGridReady = useCallback((params) => {
    loadDate();
  }, []);

  const onBtAdd = useCallback(
    (event) => {
      let newRow = {
        reference: "",
        layoutName: "",
        product: "",
        range1: "",
        priceGroup: "",
        front1: "",
        carcass: "",
        range2: "",
        priceGroup2: "",
        front2: "",
        carcass2: "",
        scheme: "",
        handleConcept: "",
        handle1: "",
        handle2: "",
        drawerAccFinish: "",
        drawerType: "",
        antiStpMat: "",
        lightings: "",
        sinkAccsseories: "",
        workTops: "",
        plinths: "",
        uploadWinnerPath: "",
        remarks: "",
      };
      const RowIndex = event.node;

      rowData && rowData?.splice(RowIndex, 0, newRow);
      gridRef.current.api.applyTransaction({
        add: [newRow],
        addIndex: RowIndex,
      });
      gridRef.current.api.refreshCells();
      setTimeout(() => {
        gridRef.current.api.setFocusedCell(RowIndex, "reference");
        gridRef.current.api.startEditingCell({
          rowIndex: RowIndex,
          colKey: "reference",
        });
      }, 800);
    },
    [rowData]
  );

  const onBtDeleteRow = useCallback(
    (event) => {
      const selectedRow = [event.node.data];

      confirmAlert({
        title: "Confirm to delete",
        message: "Are you sure to delete.",
        buttons: [
          {
            label: "Yes",
            onClick: () => {
              rowData?.splice(event.node.rowIndex, 1);
              gridRef?.current?.api.applyTransaction({ remove: selectedRow });
              selectedRow.map(async (row) => {
                const id = row?._links?.self?.href.split("/").pop();
                await OAuthService.deleteData(url, id);
                setSuccessMsg("Data deleted Successfully");
                setSnackState(true);
              });
            },
          },
          {
            label: "No",
            onClick: () => {},
          },
        ],
      });
    },
    [gridRef, rowData]
  );

  const getContextMenuItems = useCallback((event) => {
    const result = [
      {
        name: "Add",
        icon: '<span><i class="material-icons style="font-size: 18px">perm_identity</i></span>',
        action: () => {
          onBtAdd(event);
        },
      },
      {
        name: "Delete",
        icon: '<span><i class="material-icons" style="font-size: 18px">delete</i></span>',
        action: () => {
          onBtDeleteRow(event);
        },
      },
      "separator",
      "copy",
      "export",
      "chartRange",
    ];
    return result;
  }, []);

  const onRowValueChanged = async (event) => {
    const { rowIndex, data } = event.node;
    const tempAddedRows = [...addedRows];
    tempAddedRows[rowIndex] = data;
    console.log(data);
    setAddedRows(tempAddedRows);
  };

  const handleSubmit = async () => {
    console.log(addedRows);
    const rowsToUpdate = [];
    const rowsToAdd = [];

    let srNo = Math.max(...rowData.map((d) => d.srNo));

    addedRows.map((row, index) => {
      if (!row) return;
      if (!row?.srNo) {
        row.srNo = ++srNo;
        rowsToAdd.push(row);
      } else if (!!row?.srNo) {
        rowsToUpdate.push(row);
      }
    });
    rowsToAdd.map((row) => {
      OAuthService.addData(url, row)
        .then((resp) => {
          setSuccessMsg("Data Added Successfully");
          setSnackState(true);
        })
        .catch((error) => setError(error.message));
    });
    rowsToUpdate.map((row) => {
      OAuthService.updateData(url, row.srNo, row)
        .then((resp) => {
          setSuccessMsg("Data Updated Successfully");
          setSnackState(true);
        })
        .catch((error) => setError(error.message));
    });
  };

  const onBtExport = useCallback(() => {
    const params = {
      fileName: "DisplayMaster.xlsx",
      sheetName: "Display Master",
    };
    gridRef.current.api.exportDataAsExcel(params);
  }, []);

  return (
    <>
      <ListPageHeader
        componentName="Display Master"
        pageName="Display Master"
        showPaginationDropdown={showPaginationDropdown}
        // handleSubmitForReview={handleSubmitForReview}
        error={error}
        snackState={snackState}
        successMsg={successMsg}
        onBtExport={onBtExport}
        handleSubmit={handleSubmit}
      />
      <div className="h-full">
        <AgGridTable
          rowData={rowData}
          columnDefs={columnDefs}
          onGridReady={onGridReady}
          gridRef={gridRef}
          getContextMenuItems={getContextMenuItems}
          rowSelection={"multiple"}
          onCellValueChanged={onRowValueChanged}
          // onRowDoubleClicked={}
          //editType={"fullRow"}
        />
      </div>
      {/* <Button variant="contained" color="warning" size="large" onClick={handleSave}>
            Save
          </Button> */}
    </>
  );
}
